import React, { ReactElement } from 'react';

import { Button, Flex } from '@mantine/core';
import { IconRocket, IconShare2 } from '@tabler/icons-react';
import { AddSmartAssistantToStoreButton } from 'components/AddSmartAssistantToStoreButton';
import { PauseSmartAssistantButton } from 'components/PauseSmartAssistantButton';
import { TalkWithAgentButton } from 'components/TalkWithAgentButton/TalkWithAgentButton';
import { UpgradePlanButton } from 'components/UpgradePlanButton';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { SmartAssistant, SmartAssistantType } from 'shared/interfaces/SmartAssistant';
import { canOpenSmartAssistantPlayground } from 'shared/utils/playground';
import { canPauseSmartAssistant } from 'shared/utils/smart-assistant';

interface ManageSmartAssistantActionsProps {
  smartAssistant: SmartAssistant;
  onShareClick: () => void;
  onOpenPlaygroundClick: () => void;
}

export function ManageSmartAssistantActions({
  smartAssistant,
  onShareClick,
  onOpenPlaygroundClick
}: ManageSmartAssistantActionsProps) {
  const { t } = useTranslation();
  const [subscription] = useSubscriptionContext();

  const buildActions = (): ReactElement[] => {
    const components: ReactElement[] = [];

    if (smartAssistant.type === SmartAssistantType.SHOPIFY) {
      if (!subscription.product.smartAssistantIntegrations) {
        components.push(<UpgradePlanButton mr={10} size="sm" />);
      }

      components.push(
        <AddSmartAssistantToStoreButton
          disabled={!subscription.product.smartAssistantIntegrations || !smartAssistant.shopifyStoreResource}
          smartAssistant={smartAssistant}
        />
      );
    }

    if (canPauseSmartAssistant(smartAssistant)) {
      components.push(<PauseSmartAssistantButton smartAssistant={smartAssistant} />);
    }

    if (smartAssistant.type === SmartAssistantType.PHONE) {
      components.push(<TalkWithAgentButton smartAssistant={smartAssistant._id} w={180} />);
    } else if (canOpenSmartAssistantPlayground(smartAssistant)) {
      components.push(
        <Button leftIcon={<IconRocket />} onClick={onOpenPlaygroundClick}>
          {t('APP.ACTIONS.TEST')}
        </Button>
      );
    }

    // All agents can be shared
    components.push(
      <Button leftIcon={<IconShare2 />} onClick={onShareClick}>
        {t('APP.ACTIONS.SHARE')}
      </Button>
    );

    return components;
  };

  return (
    <Flex direction="row" align="center" justify="flex-end" gap="md">
      {buildActions().map((component, index) => (
        <React.Fragment key={index}>{component}</React.Fragment>
      ))}
    </Flex>
  );
}
